import React from 'react';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import { getErrorMessage, logger, showErrorToast } from 'src/utils';

export interface IWorkflowPdfFile {
  data?: Blob;
}

export interface IUseWorkflowPdfPreviewOptions<TFile extends IWorkflowPdfFile> {
  /** Renders the record's PDF without storing it. */
  fetchPdf: () => Promise<TFile | null>;
  /** Message logged when the render fails. */
  logLabel: string;
}

/**
 * @registry Drives the "Preview PDF" modal on the workflow forms: opens the modal, fetches
 * the unstored render, and exposes a blob URL for PdfViewer. Pair with WorkflowFormFooter's
 * previewOpen/previewUrl/previewLoading props.
 */
export function useWorkflowPdfPreview<TFile extends IWorkflowPdfFile>({
  fetchPdf,
  logLabel,
}: IUseWorkflowPdfPreviewOptions<TFile>) {
  const [opened, setOpened] = React.useState(false);
  const [file, setFile] = React.useState<TFile | null>(null);
  const [loading, setLoading] = React.useState(false);
  const url = usePdfBlobUrl(file?.data);

  const fetchRef = React.useRef(fetchPdf);
  fetchRef.current = fetchPdf;
  const logLabelRef = React.useRef(logLabel);
  logLabelRef.current = logLabel;

  const open = async () => {
    setOpened(true);
    setLoading(true);
    try {
      setFile(await fetchRef.current());
    } catch (err: unknown) {
      logger.error(logLabelRef.current, { err });
      showErrorToast(getErrorMessage(err));
      setOpened(false);
    }
    setLoading(false);
  };

  const close = () => {
    setOpened(false);
    setFile(null);
  };

  return { opened, url, loading, open, close };
}
